import { JobStatus } from '@gridcon-energy/schaltungstool-backend'
import type { VuetifyColors } from '~/types/vuetify-helpers'
import { JobClusterStatus } from '~/lib/sina'
import type { ResultFileType } from '@gridcon-energy/schaltungstool-backend'
import { CsvEncoding } from '@gridcon-energy/schaltungstool-backend'
import type { ResultRowKeys } from '~/lib/sina-results'
import { ViewModeEnum } from '~/lib/sina-timeline'

const ViewMode: Record<ViewModeEnum, string> = {
  [ViewModeEnum.individual]: 'Einzelne',
  [ViewModeEnum.max]: 'Max Wert',
}

const MatrixCsvEncoding: Record<CsvEncoding, string> = {
  [CsvEncoding.Cp1252]: 'Westeuropäisch (Windows-1252)',
  [CsvEncoding.Utf8Sig]: 'Unicode (UTF-8 mit BOM)',
}

export const JobStatusColors: Record<JobStatus, VuetifyColors> = {
  [JobStatus.Started]: 'info',
  [JobStatus.InProgress]: 'success-lighten-4',
  [JobStatus.HasResults]: 'success-lighten-2',
  [JobStatus.Failed]: 'error',
  [JobStatus.Finished]: 'success',
} as const

export const JobStatusTranslations: { [key in JobStatus]: string } = {
  [JobStatus.Started]: 'gestartet',
  [JobStatus.InProgress]: 'Ergebnisse werden in das Zielverzeichnis kopiert',
  [JobStatus.HasResults]: 'Ergebnisse in werden Datenbank importiert',
  [JobStatus.Failed]: 'Fehler',
  [JobStatus.Finished]: 'abgeschlossen',
}

const JobClusterStatusColorsBase: Record<JobClusterStatus, VuetifyColors> = {
  [JobClusterStatus.created]: '',
  [JobClusterStatus.running]: 'info',
  [JobClusterStatus.finished]: 'success',
  [JobClusterStatus.finished_with_errors]: 'error',
}

export const JobClusterStatusColors: Record<string, string | undefined> & typeof JobClusterStatusColorsBase =
  JobClusterStatusColorsBase

const JobClusterStatusTranslations: { [key in JobClusterStatus]: string } = {
  [JobClusterStatus.created]: 'erstellt',
  [JobClusterStatus.running]: 'in Bearbeitung',
  [JobClusterStatus.finished]: 'erledigt',
  [JobClusterStatus.finished_with_errors]: 'hat Fehler ',
}
const NNFAuswertungCNEHeaders: { [key in ResultRowKeys]: string } = {
  id: "Id des Netzobjektes {'|'} Id | Id",
  schluessel: "Integralschlüssel des Elementes  {'|'} Schlüssel | Schlüssel",
  typ: "Elementtyp {'|'} Typ | Typ",
  bezeichner: "Bezeichner  {'|'} Bez. | Bez.",
  kurzname: "Kurzname {'|'} Kurzn.| Kurzn.",
  langname: "Langname {'|'} Langn.| Langn.",
  lenge: "Länge in km {'|'} Länge | km",

  bez_so_anf: "Bezeichner SO Anfang {'|'} Bez. SO Anf. | Bez. SO Anf.",
  bez_so_end: "Bezeichner SO Ende {'|'} Bez. SO End. | Bez. SO End. ",
  ukz_so_anf: "UKZ SO Anfang {'|'} UKZ SO Anf. | UKZ SO Anf. ",
  ukz_so_end: "UKZ SO Ende {'|'} UKZ SO End. | UKZ SO End. ",
  un_anf: "Un Anfang {'|'} Un Anf. | Un Anf. ",
  un_end: "Un Ende {'|'} Un End. | Un End. ",
  ngr_anf: "Netzgruppe Anfang {'|'} Netzgr. Anf. | Netzgr. Anf. ",
  ngr_end: "Netzgruppe Ende {'|'} Netzgr. End. | Netzgr. End. ",

  anz_nnf_am: "Anzahl kritischer (CNE) NNFs  {'|'} # NNF CNE | # NNF CNE",
  max_ausl_n0: "Maximale Auslastung in % (n-0-Fall)  {'|'} Max. Ausl. % | Max. Ausl. %",
  max_ausl_n1: "Maximale Auslastung in % (n-1-Fall) {'|'} Max. Ausl. % n-1 | Max. Ausl. % n-1",
  epe: "Gesamte Engpassenergie in MVAh {'|'} EPE MVAh | EPE MVAh",
  max_nnf_n0: "NNF höchster Auslastung. (n-0-Fall) {'|'} Max. NNF | Max. NNF",
  max_nnf_n1: "NNF höchster Auslastung (n-1-Fall) {'|'} Max. NNF n-1 | Max. NNF n-1",
  bb: "Ausgefallenes Zweigelement (Schlimmste Contingency) {'|'} ausgefallen | ausgefallen",
  contingency_all: "Ausgefallenes Elemente {'|'} ausgef. alle | ausgef. alle",
  contingency_first: "Erstes ausgefallenes Element {'|'} ausgef. 1. | ausgef. 1.",

  // Extra
  bez_so: "Bezeichner SO Anfang/Ende {'|'} Bez. SO Anf/End | Bez. SO Anf/End",
  ukz_so: "UKZ SO Anfang/Ende {'|'} UKZ | UKZ",
  un: "Nennspannung Anfang/Ende {'|'} Un | Un",
  ngr: "Netzgruppe Anfang/Ende {'|'} Netzgr. | Netzgr.",
}

export const ResultFileTypeI18n: Record<ResultFileType, string> = {
  max_auslastung_zweige_n1: 'Max Auslastung N-1',
  auslastung_zweige_grundfall: 'Auslastung Grundfall',
  wirkleistung: 'Wirkleistung Grundfall',
  blindleistung: 'Blindleistung Grundfall',
  engpassleistung_zweige_n1: 'Engpassleistung N-1',
  engpassleistung_grundfall: 'Engpassleistung Grundfall',
  flussrichtung_zweige_grundfall: 'Flussrichtung Grundfall',
  flussrichtung_zweige_n1: 'Flussrichtung N-1',
  spannungsbetrag_knoten_grundfall: 'Spannungsbetrag Grundfall',
  min_spannung_knoten_n1: 'Min Spannung N-1',
  max_spannung_knoten_n1: 'Max Spannung N-1',
  scheinleistung_grundfall: 'Scheinleistung Grundfall',
  austauschleistung_netzgruppen_n0: 'Austauschleistung Netzgruppen N-0',
  berechnung: 'Berechnung',
  max_auslastung_leitungen_n1: 'Max Auslastung Leitungen N-1',
  max_auslastung_transformatoren_n1: 'Max Auslastung Transformatoren N-1',
  nnf_auswertung_cne: 'NNF-Auswertung CNE',
  nnf_auswertung_contingency: 'NNF-Auswertung Contingency',
  nnf_uebersicht: 'NNF-Übersicht',
}

export default {
  AppName: 'SINA',
  DashboardPage: 'SINA Dashboard',

  // Navigation
  NavJobClusterCreate: 'JobCluster erstellen',
  NavTableView: 'Tabellenauswertung',
  NavTimeline: 'Zeitreihen',
  NavSwitchingWindows: 'Schaltfenster',
  NavResults: 'Ergebnisse',

  // Search & Filter
  SearchProjectOrSurvey: 'Projekt oder Untersuchung',

  // Project Management
  ProjectCreate: 'Projekt',
  ProjectEdit: 'Projekt bearbeiten',
  SurveyCreate: 'Untersuchung',
  SurveyEdit: 'Untersuchung bearbeiten',
  SurveysCount: 'Keine Untersuchungen | 1 Untersuchung | {count} Untersuchungen',
  NoSurveys: 'Keine Untersuchungen vorhanden',
  NoJobClusters: 'Keine JobCluster vorhanden',
  NoResults: 'Keine Ergebnisse vorhanden',

  Results: 'Keine Ergebnisse | 1 Ergebnis | {count} Ergebnisse',
  ResultsCount: 'Keine Ergebnisse | 1 Ergebnis | {count} Ergebnisse',
  JobClusterCount: 'Keine JobCluster | 1 JobCluster | {count} JobCluster',
  RunsCount: 'Keine Durchläufe | 1 Durchlauf | {count} Durchläufe',
  TopologiesCount: 'Keine Topologien | 1 Topologie |  {count} Topologien',
  JobsCount: 'Keine Jobs | 1 Job |  {count} Jobs',
  Equipments: 'Keine Betriebsmittel | 1 Betriebsmittel |  {count} Betriebsmittel',

  // Tooltips
  TooltipRestartJobCluster: 'Fehlgeschlagene Jobs erneut starten',
  TooltipToResults: 'zu den Ergebnissen',
  TooltipCreateJobCluster: 'Neuen JobCluster erstellen',
  TooltipRefreshSurvey: 'Untersuchung Aktualisieren',

  // Table Headers
  TableHeaderJobId: 'Job Id',
  TableHeaderIntegralJobId: 'Integral Job Id',
  TableHeaderTopology: 'Topologie',
  TableHeaderStatus: 'Status',
  TableHeaderResults: 'Ergebnisse',
  TableHeaderDescription: 'Beschreibung',
  TableHeaderCreated: 'Erstellt',
  TableHeaderActions: 'Aktionen',

  // JobCluster
  JobClusterCreate: 'Neuer JobCluster',
  JobClusterMatrixPreview: 'Matrix Vorschau:',
  JobClusterDelete: 'JobCluster löschen',
  JobClusterEdit: 'JobCluster Bearbeiten',
  JobClusterLastProcessed: 'JobCluster zuletzt von Worker verarbeitet: {date}',
  JobClusterRunLabel: 'Durchlauf {count} (#{id})',
  JobClusterRunStartedAt: 'Gestartet: {date}',
  JobClusterRunLastProcessed: 'Zuletzt von Worker verarbeitet: {date}',
  JobClusterRunLastProcessedLabel: 'Zuletzt von Worker verarbeitet:',
  JobClusterRunHpcPriority: 'HPC-Priorität: {priority}',
  JobClusterOpenTargetDirectoryInAdmin: 'Zielverzeichnis im Admin-Panel öffnen',
  JobClusterRunNoJobsDiscovered: 'Es wurden noch keine JobCSV-Dateien entdeckt.',
  JobClusterTargetDirectory: 'Zielverzeichnis',
  JobClusterEditMatrix: 'Matrix bearbeiten',
  JobClusterEditMatrixDisabled: 'Matrix kann nicht bearbeitet werden, wenn JobCluster gestartet wurde',
  JobClusterViewMatrix: 'Matrix ansehen',
  JobClusterCopyMatrix: 'Matrix als Vorlage für einen neuen JobCluster kopieren',
  JobClusterRestartHint: 'Es wird ein neuer Durchlauf mit den fehlgeschlagenen Jobs des letzten Durchlaufs erstellt.',
  JobClusterRestartFailedJobsLabel: 'Fehlgeschlagene Jobs des letzten Durchlaufs: {count}',
  JobClusterFilesCreated: 'Erstellte Dateien: {files}',

  // Job
  JobDelete: 'Job Löschen',
  JobDeleteHint: 'Vorhandene Ergebnisse bleiben erhalten.',
  JobCreateCsvInCopy: 'Erstelle JobCSV in /03_in_copy',
  JobCreateCsvInCopySubtitle: 'Verschiebt entweder Datei aus /02_on_hpc oder erstellt neue.',
  JobCreateCsvCopied: 'Erstelle JobCSV in /04_copied',
  JobCreateCsvCopiedSubtitle1: 'Verschiebt entweder Datei aus /03_in_copy oder erstellt neue.',
  JobCreateCsvCopiedSubtitle2: 'Zusätzlich werden Ergebnisdatei für den Job angelegt',
  Simulate03To04DialogTitle: 'Parameter für /04_copied und Ergebnisdateien',
  Simulate03To04DialogHint: 'Konfiguration für {count} Jobs',
  Simulate03To04ArtificialEquipmentCount: 'Zusätzliche Betriebsmittelspalten',
  Simulate03To04ArtificialEquipmentCountHint: 'Anzahl zusätzlicher Spalten im Ergebnis (mindestens 0).',
  Simulate03To04TimeSeriesRows: 'Zusätzliche Zeitreihenzeilen',
  Simulate03To04TimeSeriesRowsHint: 'Optional. Leer lassen für Standardwert (mindestens 1).',
  Simulate03To04AdditionalDataPoints: 'Zusätzliche Datenpunkte: {count}',
  JobMoveCsvToFailed: 'Verschiebe JobCSV in /07_failed',
  JobMoveCsvToFailedSubtitle: 'Verschiebt entweder Dateien aus /02_on_hpc oder /03_in_copy in /07_failed',

  // Debug Actions
  DebugCreateJobCsvInCopy: 'Erstelle JobCSV in /03_in_copy ({count} Jobs)',
  DebugCreateJobCsvInCopySubtitle: 'Verschiebt entweder Datei aus /02_on_hpc oder erstellt neue.',
  DebugCreateJobCsvCopied: 'Erstelle JobCSV in /04_copied ({count} Jobs)',
  DebugCreateJobCsvCopiedSubtitle1: 'Verschiebt entweder Datei aus /03_in_copy oder erstellt neue.',
  DebugCreateJobCsvCopiedSubtitle2: 'Zusätzlich werden Ergebnisdatei für den Job angelegt',
  DebugCreateOnHpcFiles: 'Erstelle für alle Topologien in /02_on_hpc jeweils eine JobCSV-Datei',
  DebugCreateOnHpcFilesSubtitle1: 'Erfordert, dass JobCluster gestartet wurde',
  DebugCreateOnHpcFilesSubtitle2: "Danach muss 'Verarbeite /02_on_hpc Dateien' ausgeführt werden.",

  // Switching Window
  SwitchingWindowPrefix: 'Schaltfenster',
  SwitchingWindowName: 'Name',
  SwitchingWindowDuration: 'Dauer in LZ/NNF',
  SwitchingWindowDurationHint: 'Minimale Anzahl zusammenhängender LZ/NNF-Punkte',
  SwitchingWindowReferenceData: 'Bezugsdaten',
  SwitchingWindowThresholdLimit: 'Schwellwert Grenze',
  SwitchingWindowThresholdHint:
    'Nur die zur gewählten Richtung passende Grenze wird geprüft: bei Max die obere Grenze, bei Min die untere Grenze.',
  SwitchingWindowTolerance: 'Toleranz',
  SwitchingWindowToleranceHint:
    'Bei Max zählen Werte ≥ Schwellwert und < Schwellwert + Toleranz als tolerierte Überschreitung; Werte ≥ Schwellwert + Toleranz beenden das Fenster. Bei Min zählen Werte ≤ Schwellwert und > Schwellwert - Toleranz als tolerierte Überschreitung; Werte ≤ Schwellwert - Toleranz beenden das Fenster.',
  SwitchingWindowToleranceValue: 'Toleranzwert',
  SwitchingWindowMaxExceedings: 'max. Überschreitungen',
  SwitchingWindowMaxExceedingsHint:
    'Maximal erlaubte Anzahl tolerierter Überschreitungen, gezählt pro Zeitschritt (Zeile), nicht pro Betriebsmittel',
  SwitchingWindowThresholdCheck: 'Schwellwert-Prüfung',
  SwitchingWindowThresholdCheckHint: 'Min|Max - Prüfung über oder unter dem Schwellenwert',
  SwitchingWindowCalculated: 'Berechnetes Schaltfenster',
  SwitchingWindowDurationLabel: 'Dauer: {count} NNF',
  SwitchingWindowDurationLabelPlain: 'Dauer: ',
  SwitchingWindowToleranceLabel: 'Toleranz',
  SwitchingWindowThresholdLabel: 'Schwellwert',
  SwitchingWindowNoCalculated: 'Keine Schaltfenster berechnet',
  SwitchingWindowNoWindows: 'Keine Zeitfenster gefunden',
  SwitchingWindowStart: 'Start',
  SwitchingWindowEnd: 'Ende',
  SwitchingWindowHours: 'Stunden',
  SwitchingWindowSelector: 'Schaltfenster',
  SwitchingWindowNoMatchingForDataType: 'Keine passenden Schaltfenster für diesen Datentyp',
  SwitchingWindowApply: 'Anwenden',

  // File Processing
  ProcessHpcFiles: 'Verarbeite /02_on_hpc Dateien',
  ProcessInCopyFiles: 'Verarbeite /03_in_copy Dateien',
  ProcessCopiedFiles: 'Verarbeite /04_copied Dateien',
  ProcessFailedFiles: 'Verarbeite /07_Failed Dateien',
  ProcessResultFiles: 'Verarbeite Ergebnis-Dateien',
  BackgroundJobsInfo:
    'Diese Jobs werden periodisch alle 5 Sekunden im Hintergrund ausgeführt. Zu Debug-Zwecken können sie hier manuell angestoßen werden',

  // JobCluster Form
  JobClusterName: 'JobCluster Name',
  JobClusterStartHint: 'Good luck :)',
  JobClusterStartTitle: 'JobCluster starten',
  JobClusterRestartTitle: 'Fehlgeschlagene Jobs erneut starten',
  JobClusterRestartAction: 'Erneut starten',
  JobClusterHpcPriority: 'HPC-Priorität',
  JobClusterHpcPriorityHint: 'Werte von 1 bis 9. Ein kleinerer Wert bedeutet höhere Priorität.',
  JobClusterYear: 'Jahr für Jahreslauf',
  JobClusterYearHint: 'Topologien mit Jahreslauf bekommen in der Matrix-CSV Start 01.01. und Ende 31.12. dieses Jahres. Wichtig bei Schaltjahren: Der Jahreslauf hat dann 8784 statt 8760 Stunden.',
  JobClusterCsvEncoding: 'Kodierung der Matrix-CSV',
  JobClusterCsvEncodingHint: 'In dieser Kodierung wird die Matrix-CSV nach 01_Open geschrieben.',
  JobClusterStarted: 'JobCluster {name} wurde gestartet',
  JobClusterRestarted: 'Fehlgeschlagene Jobs von JobCluster {name} wurden erneut gestartet',
  JobClusterRunExportDownload: 'Matrix-CSV herunterladen',
  JobClusterRunExportDownloadSubtitle: 'Neu erzeugt aus Matrix und HPC-Priorität dieses Laufs',
  JobClusterRunExportHint:
    'Die Datei wird aus Matrix und HPC-Priorität dieses Laufs neu erzeugt. Jahr und Kodierung wurden beim Start nicht gespeichert, bitte wie beim Start angeben.',
  JobClusterRunExportNoFullYear:
    'Dieser Lauf enthält keine Topologie mit Jahreslauf. Das Jahr hat keine Wirkung auf die Datei.',
  JobClusterRunExportFilename: 'Dateiname',
  FormFieldError: 'Fehler bei "{key}" - {error}',

  MatrixCsvEncoding,

  // Matrix
  MatrixColumnExists: 'Topologie existiert bereits: {name}',
  MatrixRowExists: 'Betriebsmittel existiert bereits: {name}',
  TopologyDefaultName: 'Topologie',
  MatrixTopologies: 'Topologien',
  MatrixNewTopology: 'Neue Topologie',
  MatrixFullYear: 'Jahreslauf',
  MatrixRestored: 'Matrix wurde aus letzten Daten wiederhergestellt',
  MatrixOn: 'An',
  MatrixOff: 'Aus',
  MatrixStart: 'Start',
  MatrixCalculate: 'Berechnen',
  MatrixEquipment: 'Betriebsmittel',
  MatrixNoEquipmentAdded: 'Keine Betriebsmittel hinzugefügt',
  MatrixAddEquipmentHint: 'Fügen Sie Betriebsmittel hinzu, um die Matrix zu füllen',
  MatrixNewEquipmentTitle: 'Neue Betriebsmittel',
  MatrixNewEquipmentButton: 'Neues Betriebsmittel',
  MatrixEquipmentMultipleInfo:
    'Es können mehrere Betriebsmittel gleichzeitig angelegt werden, wenn die Integral-Namen mit "{\'|\'}" separiert eingetragen werden. In Integral können entsprechend die Namen mehrerer selektierter Betriebsmittel exportiert werden.',
  MatrixEquipmentNamePlaceholder: 'Betriebsmittelname eingeben',
  MatrixEquipmentCreate: 'Betriebsmittel Anlegen',
  MatrixMenuDuplicate: 'Duplizieren',
  MatrixMenuAllOn: 'Alle auf "Ein"',
  MatrixMenuAllOff: 'Alle auf "Aus"',
  MatrixMenuMoveLeft: 'Nach links',
  MatrixMenuMoveRight: 'Nach rechts',
  MatrixMenuMoveUp: 'Nach oben',
  MatrixMenuMoveDown: 'Nach unten',
  // Job Status & Titles
  JobStatus: JobStatusTranslations,
  JobClusterStatus: JobClusterStatusTranslations,
  JobCluster: 'JobCluster',
  ResultsManagementTitle: 'Ergebnisverwaltung',
  SwitchingWindowsTitle: 'Schaltfenster',
  ResultsTitle: 'ein Ergebnis | {count} Ergebnisse',
  ResultsDashboardSubtitle: 'Wählen Sie eine Ansicht zur Darstellung der Berechnungsergebnisse',
  ResultsTimelineTitle: 'Zeitreihen und Schaltfenster',
  ResultsTimelineDescription:
    'Visualisierung der Ergebnisse als Linien-Diagram, u.a max. Auslastung, Wirkleistung, Engpassenergie',
  ResultsTableTitle: 'Vergleich in Tabelle',
  ResultsTableDescription: 'Vergleich mehrerer Topologien und ihrer Werte auf basis von NNF-Auswertung CNE',

  // Timeline Page
  ViewMode,
  ErrorLoadingData: 'Fehler beim Laden der Daten',
  EmptyStateSelectDataType: 'Bitte wählen Sie einen Datentyp aus',
  EmptyStateSelectDataTypeDescription:
    'Wählen Sie zunächst einen Datentyp aus der Liste aus, um die Timeline-Ansicht zu aktivieren.',
  EmptyStateSelectResults: 'Bitte wählen Sie mindestens ein Ergebnis aus',
  EmptyStateSelectResultsDescription:
    'Wählen Sie ein oder mehrere Ergebnisse aus, um deren Daten im Zeitverlauf zu visualisieren.',
  EmptyStateNoData: 'Keine Daten verfügbar',
  EmptyStateNoDataDescription: 'Für die ausgewählten Ergebnisse konnten keine Daten geladen werden.',
  NotifyNoResultData: 'Keine der ausgewählten Ergebnisse hat Daten für diesen Typ',
  NotifyDataRefreshed: 'Daten aktualisiert',
  NotifyDataExported: 'Daten exportiert',
  NotifySwitchingWindowsCalculated: 'Schaltfenster für {count} Ergebnis(se) berechnet',

  EquipmentSelectedCount:
    'Keine Betriebsmittel ausgewählt | 1 Betriebsmittel ausgewählt | {count} Betriebsmittel ausgewählt',
  EquipmentSelectedAll: 'alle Betriebsmittel',
  // Results Table
  SelectResults: 'Ergebnisse auswählen',
  ValidationMaxResults: 'Maximal 5 Ergebnisse können gleichzeitig angezeigt werden',
  ResultsSelectedAll: 'alle Ergebnisse',
  ResultsSelectedCount: 'Keine Ergebnisse ausgewählt | 1 Ergebnis ausgewählt | {count} Ergebnisse ausgewählt',

  // Storage Service Notifications
  NotifyJobCreated: 'Job für {name} erstellt ({id})',
  NotifyJobStatusUpdated: 'Für Job ({id} - {name}) wurde der Status aktualisiert',
  NotifyJobResultsLoaded: 'Für Job ({id} - {name}) wurden Ergebnisdaten in die DB geladen',
  NotifyJobErrorsDetected: 'Für Job ({id} - {name}) wurden Fehler festgestellt.',

  // Switching Window Calculation
  ErrorAllCalculationsFailed: 'Alle Berechnungen sind fehlgeschlagen',
  NotifyCalculationsPartialSuccess: '{successful} von {total} Berechnungen erfolgreich. {failed} fehlgeschlagen.',

  // Chart Configuration
  ChartTooltipNNF: 'NNF',
  ChartTooltipCalendarDate: 'Kalenderdatum',
  ChartXAxisLabel: 'Stunde des Jahres (NNF)',
  ChartXAxisCalendarDateLabel: 'Kalenderdatum ({year})',
  ChartShowCalendarDateAxis: 'Kalenderdatum anzeigen',
  ChartShowNNFAxis: 'NNF anzeigen',
  ChartToolboxZoom: 'Zoom',
  ChartToolboxBack: 'Zurück',
  ChartToolboxReset: 'Zurücksetzen',
  ChartToolboxSaveImage: 'Als Bild speichern',
  ChartMarkLineToleranceLabel: 'Toleranz',
  ChartMarkLineToleranceValue: 'Toleranz: {value}',
  ChartMarkLineThresholdLabel: 'Schwellwert',
  ChartMarkLineThresholdValue: 'Schwellwert: {value}',

  NNFAuswertungCNEHeaders,
  NNCNEColumnsCommon: 'Gemeinsame Eigenschaften',
  NNCNEColumnsResults: 'Pro Ergebnis',
  ResultFileType: ResultFileTypeI18n,
  ResultFileTypeMissing: 'Ergebnisdatei {type} fehlt!',
  ResultCreate: 'Ergebnis erstellen',
  ResultEdit: 'Ergebnis bearbeiten',
  ResultName: 'Ergebnis-Name',
  ResultDescription: 'Beschreibung',
  ResultDescriptionManagedByBerechnung: 'Beschreibung wird aus Berechnung.txt übernommen',
  ResultDescriptionManagedByBerechnungHint:
    'Wenn Berechnung.txt hochgeladen wird, ersetzen die ersten 15 Zeilen den Beschreibungstext dieses Ergebnisses.',
  ResultUpload: 'Ergebnisdateien hochladen',
  ResultUploadEdit: 'Ergebnisdateien hochladen',
  ResultFileTypeSelect: 'Datei',
  ResultFileStatusColumn: 'Status',
  ResultFileSourceColumn: 'Quelle',
  ResultFileSizeColumn: 'Größe',
  ResultFilePathColumn: 'Dateipfad',
  ResultFileUploadColumn: 'Upload',
  ResultFilePresent: 'Vorhanden',
  ResultFileMissing: 'Fehlt',
  ResultFileSelected: 'Ausgewählt',
  ResultFilePending: 'Offen',
  ResultFileSourceUpload: 'Upload',
  ResultFileSourceStorageImport: 'Storage-Import',
  ResultFileSourceUnknown: 'Unbekannt',
  ResultFileSizeLabel: 'Größe: {size}',
  ResultFileUpdatedAt: 'Aktualisiert: {date}',
  ResultFilesUploaded: 'Hochgeladene Ergebnisdaten',
  ResultUploadSuccess:
    'Dateien erfolgreich hochgeladen. Sie werden nun im Hintergrund vom Server in die Datenbank übertragen.',
  ResultUploadProgress: 'Upload läuft...',
  ResultSelectFiles: 'Dateien auswählen',
  ResultReplaceHint: 'Es werden {count} von {of} dateien überschrieben. Fortfahren?',
  ResultNoFilesSelected: 'Keine Dateien ausgewählt',
  ResultUploadInProgress: 'Upload wird durchgeführt...',
  ResultUploadStepUpdateObject: 'Ergebnisobjekt aktualisieren',
  ResultUploadStepCompressFiles: 'Dateien komprimieren',
  ResultUploadStepUploadFiles: 'Dateien hochladen',
  ResultUploadStepProcessingFiles: 'Dateien verarbeiten',
  ResultDownloadStepPrepareZip: 'ZIP vorbereiten',
  ResultDownloadStepDownloadZip: 'ZIP herunterladen',
  ResultDownloadStepFinalizeZip: 'Download abschließen',
  ResultTransferRate: '{speed}/s',
  ResultTransferEta: 'Restzeit {duration}',
  ResultDownloadDuration: 'Vorbereitung: {duration}',
  ResultDownloadSuccess: 'ZIP erfolgreich heruntergeladen',
  SelectResultFolder: 'Ordner auswählen',
  SelectResultFolderHint: 'Sie können auch einen Ordner auf das Formular ziehen.',
  ResultFolderDropHint: 'Ordner hier ablegen, um Dateien für den Upload vorzumerken',
  FilesFoundInFolder: 'Dateien im Ordner gefunden',
  FilesFoundInFolderError: 'Es konnten keine Dateien im Ordner gefunden werden',
  ResultFileGroupPriority: 'Priorität',
  ResultFileGroupTimeSeries: 'Zeitreihen',
  ResultFileGroupUnused: 'Nicht verwendet',
  ResultsReference: 'Referenz',
  ResultsReferenceCannotBeShown:
    'Referenz-Ergebnis hat keine Spalten. Wählen Sie Spalten aus, die auch im Referenz-Ergebnis enthalten sind.',
  ResultsDiff: `Δ zur Referenz {'|'} Δ | Δ`,
  ResultsValue: `Wert`,
  ResultsMisc: 'Ergebnis #{count}',

  ResultsEquipmentCount: 'keine Betriebsmittel | 1 Betriebsmittel | {count} Betriebsmittel',
  HighlightCells: 'Zellen hervorheben',
  HighlightCellsConfig: 'Zellenfarben anpassen',
  GroupRows: 'Zeilen ohne Referenz-Schlüssel gruppieren',
  GroupRowsHeader: 'In allen Ergebnissen enthalten sind.',
  GroupRowsHeaderMissing: 'Schlüssel nur in {result} enthalten',

  TableScaling: 'Tabelle skalieren',
  TableSizeSmaller: 'Kleiner',
  TableSizeNormal: 'Normal',
  TableSizeLarger: 'Größer',

  // Cell Color Rules
  CellColorRules: 'Zellenfarben anpassen',
  ResetAllToDefault: 'Alles auf Standard zurücksetzen',
  ResetToDefault: 'Auf Standard zurücksetzen',
  NoRulesDefined: 'Keine Regeln definiert',
  AddRule: 'Regel hinzufügen',

  // NNF Convergence
  NNFConvergence: 'NNF Konvergenz',
  NNFConvergenceFailedAmount: 'Fehlgeschlagene NNFs',
  NNFConvergenceFailed: 'Nicht konvergiert',
  NNFConvergenceTotal: 'Gesamt',
  NNFConvergenceConverged: 'Konvergiert',
  NNFConvergenceRate: 'Konvergenzrate',
  NNFConvergenceCount: '✓ Alle konvergiert | ⚠ 1 NNF nicht konvergiert | ⚠ {count} NNFs nicht konvergiert',
  NNFConvergenceTooltip: 'Anzahl der NNFs die nicht konvergiert sind',
  NNFConvergenceDetails: '{failed} von {total} NNFs nicht konvergiert',

  // Result Read
  ResultReadID: 'ID',
  ResultReadName: 'Name',
  ResultReadBeschreibung: 'Beschreibung',
  ResultReadCreatedAt: 'Erstellt am',
  ResultReadUpdatedAt: 'Aktualisiert am',
  ResultReadResults: 'Ergebnisdaten',
  ResultReadTotalSize: 'Gesamtgröße',
}
